import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Space,
  Tag,
  message,
  Popconfirm,
  Tooltip,
  Badge,
  Row,
  Col,
  Alert
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  PhoneOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import {
  whatsappConnectorService,
  WhatsAppAccount,
  CreateWhatsAppAccountRequest,
  UpdateWhatsAppAccountRequest,
  WhatsAppPartner
} from '../services/whatsappConnectorService';
import TimeDisplay from './TimeDisplay';
import { useI18n } from '../hooks/useI18n';
import './WhatsAppConnectorManager.css';

const { Option } = Select;
const { TextArea } = Input;

const WhatsAppAccountsPage: React.FC = () => {
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<WhatsAppAccount[]>([]);
  const [partners, setPartners] = useState<WhatsAppPartner[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState<WhatsAppAccount | null>(null);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<WhatsAppAccount | null>(null);

  const [form] = Form.useForm();
  const [testForm] = Form.useForm();
  const { t } = useI18n();

  // 加载数据
  const loadAccounts = async () => {
    setLoading(true);
    try {
      const response = await whatsappConnectorService.getWhatsAppAccounts();
      setAccounts(response.data || []);
    } catch (error: any) {
      console.error('加载WhatsApp账号失败:', error);
      message.error('加载WhatsApp账号失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 加载Partner数据
  const loadPartners = async () => {
    try {
      const response = await whatsappConnectorService.getWhatsAppPartners();
      setPartners(response.data || []);
    } catch (error: any) {
      console.error('加载Partner列表失败:', error);
    }
  };

  useEffect(() => {
    loadAccounts();
    loadPartners();
  }, []);

  // 表格列配置
  const columns = [
    {
      title: '账号名称',
      dataIndex: 'account_name',
      key: 'account_name',
      width: 150,
    },
    {
      title: '电话号码',
      dataIndex: 'phone_number',
      key: 'phone_number',
      width: 150,
      render: (phone: string) => (
        <Space>
          <PhoneOutlined />
          {phone}
        </Space>
      ),
    },
    {
      title: '账号类型',
      dataIndex: 'account_type',
      key: 'account_type',
      width: 100,
      render: (type: string) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active: boolean) => (
        <Badge
          status={active ? 'success' : 'error'}
          text={active ? '活跃' : '停用'}
        />
      ),
    },
    {
      title: 'Partner',
      dataIndex: 'partner_name',
      key: 'partner_name',
      width: 150,
      render: (partnerName: string, record: WhatsAppAccount) => {
        if (record.partner_id && partnerName) {
          return <Tag color="green">{partnerName}</Tag>;
        }
        return <Tag color="default">未分配</Tag>;
      },
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      render: (priority: number) => <Tag color="orange">{priority}</Tag>,
    },
    {
      title: '最大并发',
      dataIndex: 'max_concurrent_chats',
      key: 'max_concurrent_chats',
      width: 100,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time: string) => time ? <TimeDisplay utcTime={time} format="datetime" /> : '-',
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160,
      render: (time: string) => time ? <TimeDisplay utcTime={time} format="datetime" /> : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: any, record: WhatsAppAccount) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="发送测试消息">
            <Button
              type="link"
              icon={<SendOutlined />}
              onClick={() => handleSendTestMessage(record)}
            />
          </Tooltip>
          <Tooltip title="删除">
            <Popconfirm
              title="确定删除这个WhatsApp账号吗？"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 处理函数
  const handleCreate = async (values: any) => {
    try {
      const tenant_code = localStorage.getItem('currentTenant') || 'default';
      const payload: CreateWhatsAppAccountRequest = {
        tenant_code,
        accountName: values.accountName,
        accountType: values.accountType,
        phoneNumber: values.phoneNumber,
        webhookUrl: values.webhookUrl,
        webhookToken: values.webhookToken,
        accessToken: values.accessToken,
        verifyToken: values.verifyToken,
        businessAccountId: values.businessAccountId,
        priority: values.priority,
        maxConcurrentChats: values.maxConcurrentChats,
        workingHours: values.workingHours,
        autoReplyConfig: values.autoReplyConfig,
        isActive: values.isActive,
      };
      // 仅在创建时，如有 partner 选择，可附加；后端 create 接口当前未显式接收 partnerId，若需要后端支持可同步调整
      (payload as any).partnerId = values.partner_id;

      console.log('Create WhatsApp Account payload:', payload);
      await whatsappConnectorService.createWhatsAppAccount(payload);
      message.success('WhatsApp账号创建成功');
      setModalVisible(false);
      form.resetFields();
      loadAccounts();
    } catch (error: any) {
      message.error('创建失败: ' + error.message);
    }
  };

  const handleEdit = (account: WhatsAppAccount) => {
    setEditingAccount(account);
    // 将 snake_case 字段名转换为 camelCase 以匹配表单字段名
    const formData = {
      accountName: account.account_name,
      accountType: account.account_type,
      phoneNumber: account.phone_number,
      partner_id: account.partner_id,
      webhookUrl: account.webhook_url,
      webhookToken: account.webhook_token,
      accessToken: account.access_token,
      verifyToken: account.verify_token,
      businessAccountId: account.business_account_id,
      priority: account.priority,
      maxConcurrentChats: account.max_concurrent_chats,
      workingHours: account.working_hours ? JSON.parse(account.working_hours) : {},
      autoReplyConfig: account.auto_reply_config ? JSON.parse(account.auto_reply_config) : {},
      isActive: Boolean(account.is_active)
    };
    form.setFieldsValue(formData);
    setModalVisible(true);
  };

  const handleUpdate = async (values: any) => {
    if (!editingAccount) return;

    try {
      const payload: UpdateWhatsAppAccountRequest = {
        accountName: values.accountName,
        accountType: values.accountType,
        phoneNumber: values.phoneNumber,
        webhookUrl: values.webhookUrl,
        webhookToken: values.webhookToken,
        accessToken: values.accessToken,
        verifyToken: values.verifyToken,
        businessAccountId: values.businessAccountId,
        priority: values.priority,
        maxConcurrentChats: values.maxConcurrentChats,
        workingHours: values.workingHours,
        autoReplyConfig: values.autoReplyConfig,
        isActive: values.isActive,
      } as UpdateWhatsAppAccountRequest;
      // partner 映射
      (payload as any).partnerId = values.partner_id;

      console.log('Update WhatsApp Account payload:', payload);
      await whatsappConnectorService.updateWhatsAppAccount(editingAccount.id, payload);
      message.success('WhatsApp账号更新成功');
      setModalVisible(false);
      setEditingAccount(null);
      form.resetFields();
      loadAccounts();
    } catch (error: any) {
      message.error('更新失败: ' + error.message);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await whatsappConnectorService.deleteWhatsAppAccount(id);
      message.success('WhatsApp账号删除成功');
      loadAccounts();
    } catch (error: any) {
      message.error('删除失败: ' + error.message);
    }
  };

  const handleSendTestMessage = (account: WhatsAppAccount) => {
    setSelectedAccount(account);
    setTestModalVisible(true);
  };

  const handleSendTest = async (values: any) => {
    if (!selectedAccount) return;

    try {
      const result = await whatsappConnectorService.sendTestMessage(
        selectedAccount.id,
        values.to,
        values.message
      );
      message.success(`测试消息发送成功: ${result.messageId}`);
      setTestModalVisible(false);
      testForm.resetFields();
      setSelectedAccount(null);
    } catch (error: any) {
      message.error('发送失败: ' + error.message);
    }
  };

  return (
    <div className="whatsapp-connector-container">
      <Card className="whatsapp-connector-card">
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, color: '#1890ff' }}>WhatsApp账号管理</h2>
            <p style={{ margin: '8px 0 0 0', color: '#666' }}>
              管理您的WhatsApp Business账号配置
            </p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingAccount(null);
              form.resetFields();
              setModalVisible(true);
            }}
            className="whatsapp-connector-button-primary"
          >
            新建WhatsApp账号
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={accounts}
          rowKey="id"
          loading={loading}
          className="whatsapp-connector-table"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            pageSize: 10,
          }}
          scroll={{ x: 1200 }}
        />

        {accounts.length === 0 && !loading && (
          <div className="whatsapp-connector-empty">
            <PhoneOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
            <p style={{ margin: '16px 0', color: '#999' }}>暂无WhatsApp账号</p>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingAccount(null);
                form.resetFields();
                setModalVisible(true);
              }}
            >
              创建第一个账号
            </Button>
          </div>
        )}
      </Card>

      {/* 新建/编辑模态框 */}
      <Modal
        title={
          <Space>
            <PhoneOutlined />
            {editingAccount ? '编辑WhatsApp账号' : '新建WhatsApp账号'}
          </Space>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingAccount(null);
          form.resetFields();
        }}
        footer={null}
        width={800}
        className="whatsapp-connector-modal"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(vals) => {
            const latest = form.getFieldsValue(true);
            console.log('WhatsAppAccounts submit values:', vals);
            console.log('WhatsAppAccounts latest form values:', latest);
            return (editingAccount ? handleUpdate : handleCreate)(latest);
          }}
          className="whatsapp-connector-form"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="accountName"
                label="账号名称"
                rules={[{ required: true, message: '请输入账号名称' }]}
              >
                <Input placeholder="请输入账号名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="accountType"
                label="账号类型"
                rules={[{ required: true, message: '请选择账号类型' }]}
              >
                <Select placeholder="请选择账号类型">
                  <Option value="business">Business</Option>
                  <Option value="personal">Personal</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phoneNumber"
                label="电话号码"
                rules={[{ required: true, message: '请输入电话号码' }]}
              >
                <Input placeholder="请输入电话号码" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="partner_id"
                label="Partner"
                rules={[{ required: true, message: '请选择Partner' }]}
              >
                <Select placeholder="请选择Partner" allowClear>
                  {partners.map(partner => (
                    <Option key={partner.id} value={partner.id}>
                      {partner.partnerName} ({partner.partnerType})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="priority"
                label="优先级"
                rules={[{ required: true, message: '请输入优先级' }]}
              >
                <Select placeholder="请选择优先级">
                  <Option value={1}>高 (1)</Option>
                  <Option value={2}>中 (2)</Option>
                  <Option value={3}>低 (3)</Option>
                  <Option value={4}>最低 (4)</Option>
                  <Option value={5}>禁用 (5)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="maxConcurrentChats"
                label="最大并发对话数"
                rules={[{ required: true, message: '请输入最大并发对话数' }]}
              >
                <Select placeholder="请选择最大并发对话数">
                  <Option value={10}>10</Option>
                  <Option value={20}>20</Option>
                  <Option value={50}>50</Option>
                  <Option value={100}>100</Option>
                  <Option value={200}>200</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="businessAccountId" label="Business Account ID">
            <Input placeholder="请输入Business Account ID" />
          </Form.Item>

          <Form.Item name="webhookUrl" label="Webhook URL">
            <Input placeholder="请输入Webhook URL" />
          </Form.Item>

          <Form.Item name="accessToken" label="Access Token">
            <TextArea
              rows={3}
              placeholder="请输入Access Token"
              showCount
              maxLength={1000}
            />
          </Form.Item>

          <Form.Item name="verifyToken" label="Verify Token">
            <Input placeholder="请输入Verify Token" />
          </Form.Item>

          <Form.Item name="isActive" label="是否启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  setEditingAccount(null);
                  form.resetFields();
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingAccount ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 测试消息模态框 */}
      <Modal
        title={
          <Space>
            <SendOutlined />
            发送测试消息
          </Space>
        }
        open={testModalVisible}
        onCancel={() => {
          setTestModalVisible(false);
          setSelectedAccount(null);
          testForm.resetFields();
        }}
        footer={null}
        width={600}
        className="whatsapp-connector-modal"
      >
        <Form
          form={testForm}
          layout="vertical"
          onFinish={handleSendTest}
          className="whatsapp-connector-form"
        >
          {selectedAccount && (
            <Alert
              message={`测试账号: ${selectedAccount.account_name}`}
              description={`电话号码: ${selectedAccount.phone_number}`}
              type="info"
              showIcon
              className="whatsapp-connector-alert"
            />
          )}

          <Form.Item
            name="to"
            label="接收者电话号码"
            rules={[
              { required: true, message: '请输入接收者电话号码' },
              {
                pattern: /^\+[1-9]\d{1,14}$/,
                message: '请输入正确的国际电话号码格式(例如: +1234567890)'
              }
            ]}
          >
            <Input placeholder="请输入接收者电话号码(国际格式)" />
          </Form.Item>

          <Form.Item
            name="message"
            label="消息内容"
            rules={[{ required: true, message: '请输入消息内容' }]}
          >
            <TextArea
              rows={4}
              placeholder="请输入测试消息内容"
              showCount
              maxLength={1000}
            />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button
                onClick={() => {
                  setTestModalVisible(false);
                  setSelectedAccount(null);
                  testForm.resetFields();
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                发送测试消息
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WhatsAppAccountsPage;
